import { useForm } from 'react-hook-form'

import { useEffect, useState } from 'react'

import { useFetchRoles } from '@/hooks/features/roles'

import { UserModel as UserType } from '@/types'

import { UserFormState, userFieldsConfig } from './userFieldsConfig'

interface UseUserFormProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editUser: UserType.User | null
  onCreate: (data: UserFormState) => void
  onUpdate: (id: string, data: UserFormState) => void
}

export const useUserForm = ({ open, mode, editUser, onCreate, onUpdate }: UseUserFormProps) => {
  const [processing, setProcessing] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState(false)

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
    control,
  } = useForm<UserFormState>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role_ids: [],
      status: 'active',
      image_id: '',
      image_url_temp: '',
      password: '',
      permissions: [],
    },
  })

  // Use sub-watching for better reactivity in the UI
  const userPermissions = watch('permissions') || []
  const selectedRoleIds = watch('role_ids') || []

  const { data: rolesData, loading: rolesLoading } = useFetchRoles()
  const { fields } = userFieldsConfig()

  const rolePermissions = (rolesData || [])
    .filter((role) => (selectedRoleIds || []).includes(role._id))
    .reduce((acc, role) => {
      const perms = role.permissions || []
      return [...new Set([...acc, ...perms])]
    }, [] as string[])

  useEffect(() => {
    if (open) {
      if ((mode === 'edit' || mode === 'view') && editUser) {
        // Derive role IDs if missing but roles are populated
        const derivedRoleIds = editUser.role_ids || editUser.roles?.map((r: any) => r._id || r) || []

        reset({
          name: editUser.name,
          email: editUser.email || '',
          phone: editUser.phone || '',
          role_ids: derivedRoleIds,
          status: editUser.status === 'active' ? 'active' : 'inactive',
          image_id:
            editUser.image?._id || (typeof editUser.image === 'string' ? editUser.image : ''),
          image_url_temp: editUser.image?.path || '',
          password: '',
          permissions: editUser.permissions || [],
        })
      } else {
        reset({
          name: '',
          email: '',
          phone: '',
          role_ids: [],
          status: 'active',
          image_id: '',
          image_url_temp: '',
          password: '',
          permissions: [],
        })
      }
    }
  }, [open, mode, editUser, reset])

  const onSubmit = async (data: UserFormState) => {
    setProcessing(true)
    try {
      const payload = { ...data }
      delete payload.image_url_temp
      if (!payload.email) delete payload.email
      if (!payload.phone) delete payload.phone
      if (mode === 'edit') delete payload.password

      if (mode === 'create') {
        onCreate(payload)
      } else {
        if (!editUser?._id) return
        onUpdate(editUser._id, payload)
      }
    } finally {
      setProcessing(false)
    }
  }

  const roleOptions = (rolesData || []).map((role) => ({
    label: role.name,
    value: role._id,
  }))

  const formValues = watch()

  const updatePermission = (key: string, type: 'allow' | 'deny' | 'inherit') => {
    if (mode === 'view') return
    
    // Always get the latest values directly from the form state to avoid closures
    const current = getValues('permissions') || []
    
    // Filter out both explicit allow ("key") and explicit deny ("key:forbidden")
    let next = current.filter((p) => p !== key && p !== `${key}:forbidden`)

    if (type === 'allow') {
      next.push(key)
    } else if (type === 'deny') {
      next.push(`${key}:forbidden`)
    }

    setValue('permissions', [...next], { 
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true 
    })
  }

  const getPermissionState = (key: string) => {
    const perms = userPermissions || []
    if (perms.includes(key)) return 'allow'
    if (perms.includes(`${key}:forbidden`)) return 'deny'
    return 'inherit'
  }

  const isAllowedByRole = (key: string) => rolePermissions.includes(key)

  return {
    control,
    handleSubmit,
    setValue,
    watch,
    errors,
    processing,
    galleryOpen,
    setGalleryOpen,
    formValues,
    rolesLoading,
    roleOptions,
    fields,
    updatePermission,
    getPermissionState,
    isAllowedByRole,
    onSubmit,
  }
}
